<template>
  <layout-report-step
    :isDone="isDone"
    :title="header.title"
    :description="header.description"
    :image="header.image"
    :debug="header.debug"
  >
    <v-card flat>
      <v-radio-group v-model="liabilityDamage" mandatory>
        <v-card flat outlined class="pa-2 pt-4 my-2">
          <v-radio :value="CoverageEnum.SELF_PAYER">
            <template v-slot:label>
              <strong>{{ $t("report.liabilityDamage.cards.selfPayer.title") }}</strong>
            </template>
          </v-radio>
          <v-card-text class="ml-4">{{ $t("report.liabilityDamage.cards.selfPayer.description") }}</v-card-text>
        </v-card>

        <v-card flat outlined class="pa-2 pt-4 my-2">
          <v-radio :value="CoverageEnum.INSURANCE" @click="$vuetify.goTo('#scrollmarker')">
            <template v-slot:label>
              <strong>{{ $t("report.liabilityDamage.cards.insurance.title") }}</strong>
            </template>
          </v-radio>
          <v-card-text class="ml-4">{{ $t("report.liabilityDamage.cards.insurance.description") }}</v-card-text>
        </v-card>

        <v-card flat outlined class="pa-2 pt-4 my-2">
          <v-radio :value="CoverageEnum.INSURANCE_OPPONENT" @click="$vuetify.goTo('#scrollmarker')">
            <template v-slot:label>
              <div>
                <strong>{{ $t("report.liabilityDamage.cards.insuranceOpponent.title") }}</strong>
              </div>
            </template>
          </v-radio>
          <v-card-text class="ml-4">{{ $t("report.liabilityDamage.cards.insuranceOpponent.description") }}</v-card-text>
        </v-card>
      </v-radio-group>
    </v-card>
    <span id="scrollmarker"></span>
    <v-card v-if="liabilityDamage == CoverageEnum.INSURANCE_OPPONENT" flat>
      <v-card-title>{{ $t("report.liabilityDamage.liable.title") }}</v-card-title>
      <witness-form
        v-model="witness"
        :role="ReportRoleEnum.RESPONSIBLE"
        :show-insurance="true"
        :show-injury="false"
        :required="false"
        @valid="($e) => (isOpponentValid = $e)"
      ></witness-form>
    </v-card>

    <v-card v-if="liabilityDamage == CoverageEnum.INSURANCE" flat>
      <v-card-title>{{ $t("report.liabilityDamage.cards.insurance.title") }}</v-card-title>

      <insurance-form
        v-model="insurance"
        :types="insuranceTypes"
        :required="true"
        @valid="($e) => (isInsuranceValid = $e)"
      ></insurance-form>
    </v-card>
  </layout-report-step>
</template>

<script lang="ts">
import InsuranceForm, { InsuranceTypeEnum } from "@/components/report/InsuranceForm.vue";
import WitnessForm from "@/components/report/WitnessForm.vue";
import LayoutReportStep from "@/layouts/LayoutReportStep.vue";
import { CoverageEnum } from "@/lib/enum/liability.enum";
import { ReportRoleEnum } from "@/lib/enum/ReportRole.enum";
import { getDefaultPartnerColor } from "@/lib/getDefaultPartnerColor";
import { IReportHeaderData } from "@/lib/interfaces/Report/IReportHeaderData";
import { IReportStep } from "@/lib/interfaces/Report/IViewReportStep";
import ReportStepMixin from "@/mixins/ReportStepMixin.vue";
import {
  MrfiktivCreateInsuranceDtoGen,
  MrfiktivCreateReportWitnessDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { ReportScreenEnum } from "@/store/enum/partner/report-screen.enum";
import { ReportModule } from "@/store/modules/report.store";
import { Component } from "vue-property-decorator";

@Component({
  components: {
    LayoutReportStep,
    InsuranceForm,
    WitnessForm
  }
})
export default class ReportLiabilityDamage extends ReportStepMixin implements IReportStep {
  header: IReportHeaderData = {
    title: "report.liabilityDamage.title",
    description: "report.liabilityDamage.description"
  };
  screen = ReportScreenEnum.liabilityselector;

  valid = false;
  isInsuranceValid = false;
  isOpponentValid = false;

  InsuranceTypeEnum = InsuranceTypeEnum;
  ReportRoleEnum = ReportRoleEnum;
  CoverageEnum = CoverageEnum;

  witnessTemp: MrfiktivCreateReportWitnessDtoGen = {
    firstName: "",
    lastName: "",
    contact: {
      email: "",
      phone: ""
    },
    imageIds: [],
    role: ReportRoleEnum.RESPONSIBLE,
    insurances: []
  };

  insuranceTemp: MrfiktivCreateInsuranceDtoGen = {
    name: "",
    number: "",
    type: InsuranceTypeEnum.MOTORISTS_THIRD_PARTY_LIABILITY_COVERAGE
  };

  mounted() {
    this.liabilityDamage = ReportModule.coverage ?? CoverageEnum.SELF_PAYER;
  }

  get body() {
    return ReportModule.body;
  }

  get liabilityDamage() {
    return ReportModule.coverage ?? CoverageEnum.SELF_PAYER;
  }

  set liabilityDamage(liability: CoverageEnum) {
    if (liability === CoverageEnum.INSURANCE_OPPONENT) {
      // Preserve existing witness data on re-visit; only seed the template on first selection.
      if (!ReportModule.liableWitness) {
        ReportModule.setLiableWitness(this.witnessTemp);
      }
      ReportModule.setInsuranceList([]);
    }
    if (liability === CoverageEnum.INSURANCE) {
      ReportModule.setLiableWitness(undefined);
    }

    if (liability === CoverageEnum.SELF_PAYER) {
      ReportModule.setLiableWitness(undefined);
      ReportModule.setInsuranceList([]);
    }

    ReportModule.setCoverage(liability);
  }

  get isDone() {
    if (this.liabilityDamage === CoverageEnum.INSURANCE_OPPONENT) {
      return this.isOpponentValid;
    }

    if (this.liabilityDamage === CoverageEnum.INSURANCE) {
      return this.isInsuranceValid;
    }

    return true;
  }

  get color() {
    return getDefaultPartnerColor();
  }

  get insuranceTypes() {
    return [InsuranceTypeEnum.FULLY_COMPREHENSIVE_COVERAGE, InsuranceTypeEnum.PARTIALLY_COMPREHENSIVE_COVERAGE];
  }

  get witness() {
    return ReportModule.liableWitness;
  }

  set witness(liableWitness: MrfiktivCreateReportWitnessDtoGen | undefined) {
    ReportModule.setLiableWitness(liableWitness);
  }

  get insurance() {
    return ReportModule.insuranceList[0];
  }

  set insurance(insurance: MrfiktivCreateInsuranceDtoGen | undefined) {
    if (insurance) {
      ReportModule.setInsuranceList([insurance]);
    } else {
      ReportModule.setInsuranceList([]);
    }
  }
}
</script>

<style scoped>
.center {
  text-align: center;
}
</style>
